// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * wordlist.ts — built-in weak HMAC secrets.
 *
 * A curated list of the secrets that actually show up in leaked HS256 tokens:
 * framework defaults, tutorial copy-paste values, and trivially guessable
 * strings. The point of the crack feature is to catch these before an attacker
 * does. A "not found" result is NOT proof a secret is strong — it only proves
 * it isn't in this list.
 */

export const WEAK_SECRETS: string[] = [
  // framework / tutorial defaults
  'secret',
  'your-256-bit-secret',
  'your_jwt_secret',
  'your-secret-key',
  'jwt_secret',
  'jwtsecret',
  'jwtSecret',
  'JWT_SECRET',
  'mysecret',
  'mySecret',
  'supersecret',
  'super_secret',
  'superSecretKey',
  'topsecret',
  'changeme',
  'change_me',
  'changethis',
  'please-change-me',
  'default',
  'defaultsecret',
  'example',
  'example_key',
  'test',
  'testing',
  'testkey',
  'test_secret',
  'demo',
  'placeholder',
  'replace-with-your-secret',
  // generic passwords
  'password',
  'Password',
  'password1',
  'password123',
  'passw0rd',
  'admin',
  'administrator',
  'root',
  'toor',
  'qwerty',
  'qwerty123',
  'letmein',
  'welcome',
  'welcome1',
  'iloveyou',
  'monkey',
  'dragon',
  'master',
  'login',
  'abc123',
  'a',
  'aa',
  'aaa',
  // digit runs
  '0',
  '1',
  '12',
  '123',
  '1234',
  '12345',
  '123456',
  '1234567',
  '12345678',
  '123456789',
  '1234567890',
  '111111',
  '000000',
  '666666',
  '654321',
  // common app names / branding placeholders
  'app',
  'application',
  'api',
  'api_key',
  'apikey',
  'token',
  'tokensecret',
  'auth',
  'authsecret',
  'authentication',
  'session',
  'sessionsecret',
  'session_secret',
  'signature',
  'sign',
  'signingkey',
  'signing-key',
  'privatekey',
  'private_key',
  'publickey',
  'key',
  'mykey',
  'thekey',
  'thisisasecret',
  'thisismysecret',
  'shhh',
  'shh',
  'hunter2',
  'foobar',
  'foo',
  'bar',
  'baz',
  'qux',
  'helloworld',
  'hello',
  'world',
  // library defaults seen in the wild
  'GQDstcKsx0NHjPOuXOYg5MbeJ1XT0uFiwDVvVBrk',
  'eyJhbGciOiJIUzI1NiJ9',
  'devsecret',
  'dev',
  'development',
  'staging',
  'production',
  'prod',
  'local',
  'localhost',
  'CHANGE_ME',
  'CHANGE_THIS_SECRET',
  'INSERT_YOUR_SECRET_HERE',
  'replaceme',
  'notsosecret',
  'verysecret',
  'ssssh',
  'secretkey',
  'secret_key',
  'secret123',
  'Secret',
  'SECRET',
  's3cr3t',
  's3cret',
  'sup3rs3cr3t',
  'p@ssw0rd',
  'P@ssw0rd',
  'qazwsx',
  'zxcvbn',
  'asdfgh',
  'trustno1',
  'jsonwebtoken',
  'jwt',
  'JWT',
  'bearer',
  'oauth',
  'keyboardcat',
  'keyboard cat',
  'cat',
  'ABCDEFGH',
  'abcdefgh',
];

/** De-duplicated, with a couple of trivial transforms folded in. */
export function defaultWordlist(): string[] {
  const set = new Set<string>(WEAK_SECRETS);
  return [...set];
}
